import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  LinearProgress,
  MenuItem,
  Pagination,
  Paper,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { keyframes } from "@emotion/react";
import CustomInstructionBox from "../../../../../customComponents/CustomInstructionBox";
import UserAvatar from "../../../../common/UserAvatar";
import SportsEsportsIcon from "@mui/icons-material/SportsEsports";
import SchoolIcon from "@mui/icons-material/School";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import AutoStoriesIcon from "@mui/icons-material/AutoStories";
import PlayTransition from "./PlayTransition";
import CustomInfoCard from "../../../../../customComponents/CustomInfoCard";
import QuizIcon from "../../../../../assets/icons/quiz-icon.svg";
import React from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import CustomResultModal from "../../../../../customComponents/CustomResultModal";
import {
  useGetQuizzesPaginatedQuery,
  useGetQuizResultQuery,
} from "../../../../../app/quizApiSlice";
import { getActiveQuizSession, saveActiveQuizSession, clearActiveQuizSession } from "../../../../../helpers/activeQuizSession";
import { useGetDuelByQuizIdQuery } from "../../../../../app/duelApiSlice";
import { useGetTournamentByQuizIdQuery } from "../../../../../app/tournamentApiSlice";
import { useGetLearnSessionByQuizIdQuery } from "../../../../../app/learnApiSlice";
import MathContent from "../../../../customComponents/MathContent";
import CloseIcon from "@mui/icons-material/Close";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import MenuBookIcon from "@mui/icons-material/MenuBook";

// ─── Learn Lesson Modal ───────────────────────────────────────────────────────
const LearnLessonModal = ({ quizId, open, onClose }) => {
  const { data, isLoading } = useGetLearnSessionByQuizIdQuery(quizId, { skip: !quizId || !open });
  const session = data?.session;
  const allBlocks = Array.isArray(session?.content) ? session.content : [];
  const textBlocks = allBlocks.filter((b) => b.type !== "youtube");
  const youtubeBlocks = allBlocks.filter((b) => b.type === "youtube");

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth scroll="paper" style={{ zIndex: 1500 }}
      PaperProps={{ sx: { borderRadius: "16px", maxHeight: "90vh" } }}
    >
      <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", pb: 1 }}>
        <Box>
          <Typography sx={{ fontWeight: 800, fontSize: "1.1rem" }}>
            {session?.subject || "Lesson"}
          </Typography>
          {session?.topics?.length > 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.3 }}>
              {session.topics.join(" · ")}
            </Typography>
          )}
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ p: { xs: 2, sm: 3 } }}>
        {isLoading && <Box sx={{ textAlign: "center", py: 4 }}><CircularProgress sx={{ color: "#7C3AED" }} /></Box>}
        {!isLoading && !session && (
          <Typography color="text.secondary" sx={{ textAlign: "center", py: 4 }}>Lesson content not found.</Typography>
        )}
        {!isLoading && session && (
          <Box>
            {textBlocks.map((block, i) => (
              <Paper key={i} elevation={0} sx={{ mb: 2.5, borderRadius: "14px", border: "1.5px solid #ede9fe", overflow: "hidden" }}>
                <Box sx={{ px: 2.5, py: 1.2, bgcolor: "#f3e8ff", borderBottom: "1.5px solid #ede9fe", display: "flex", alignItems: "center", gap: 1 }}>
                  <MenuBookIcon sx={{ fontSize: 16, color: "#7C3AED" }} />
                  <Typography sx={{ fontWeight: 700, fontSize: "0.92rem", color: "#5B21B6" }}>
                    {block.topic || block.heading || `Section ${i + 1}`}
                  </Typography>
                </Box>
                <Box sx={{ px: 2.5, py: 2, fontSize: "0.95rem", lineHeight: 1.85, color: "#1e293b",
                  "& p": { mb: 1 }, "& ul,& ol": { pl: 2.5 }, "& strong": { fontWeight: 700, color: "#6d28d9" },
                  "& h2,& h3": { fontWeight: 700, color: "#6d28d9", mt: 1.5, mb: 0.5 },
                }}>
                  <MathContent html={block.content || block.body || ""} />
                </Box>
              </Paper>
            ))}

            {youtubeBlocks.map((block, i) => (
              <Paper key={`yt-${i}`} elevation={0} sx={{ mb: 2.5, borderRadius: "14px", border: "1.5px solid #fee2e2", overflow: "hidden" }}>
                <Box sx={{ px: 2.5, py: 1.2, bgcolor: "#fff1f2", borderBottom: "1.5px solid #fee2e2", display: "flex", alignItems: "center", gap: 1 }}>
                  <PlayCircleOutlineIcon sx={{ fontSize: 18, color: "#DC2626" }} />
                  <Typography sx={{ fontWeight: 700, fontSize: "0.92rem", color: "#991b1b" }}>
                    {block.title || block.topic || "Related Video"}
                  </Typography>
                </Box>
                <Box sx={{ position: "relative", paddingBottom: "56.25%" }}>
                  <iframe
                    style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%" }}
                    src={`https://www.youtube.com/embed/${block.video_id}`}
                    title={block.title || "Learn video"}
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                </Box>
              </Paper>
            ))}
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

const joystickWiggle = keyframes`
  0%   { transform: rotate(0deg)  scale(1); }
  10%  { transform: rotate(-18deg) scale(1.15); }
  20%  { transform: rotate(18deg)  scale(1.15); }
  30%  { transform: rotate(-14deg) scale(1.1); }
  40%  { transform: rotate(14deg)  scale(1.1); }
  50%  { transform: rotate(-8deg)  scale(1.05); }
  60%  { transform: rotate(8deg)   scale(1.05); }
  70%  { transform: rotate(-4deg)  scale(1); }
  80%  { transform: rotate(4deg)   scale(1); }
  90%  { transform: translateY(-6px) scale(1); }
  100% { transform: translateY(0)   scale(1); }
`;

const getInitials = (name = "") =>
  name.split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2);

const DuelResultModal = ({ quizId, open, onClose }) => {
  const authData = useSelector((state) => state.auth);
  const myUserId = Number(authData?.user?.id);

  const { data: duelByQuizData, isLoading: loadingDuel } = useGetDuelByQuizIdQuery(
    quizId, { skip: !quizId || !open }
  );
  const duel = duelByQuizData?.duel;

  const { data: myResult, isLoading: loadingResult } = useGetQuizResultQuery(
    { quiz_id: quizId || 0 }, { skip: !quizId || !open }
  );

  const iAmPlayerOne = Number(duel?.player_one_id) === myUserId;
  const myPoints  = iAmPlayerOne ? duel?.player_one_points : duel?.player_two_points;
  const oppPoints = iAmPlayerOne ? duel?.player_two_points : duel?.player_one_points;
  const mePlayer  = iAmPlayerOne ? duel?.player_one : duel?.player_two;
  const oppPlayer = iAmPlayerOne ? duel?.player_two : duel?.player_one;

  const getDisplayName = (user) => {
    const p = user?.auth_profile;
    if (p?.first_name || p?.last_name) return `${p.first_name || ""} ${p.last_name || ""}`.trim();
    return user?.username || "—";
  };

  const myName  = getDisplayName(mePlayer) || "You";
  const oppName = getDisplayName(oppPlayer) || "Opponent";

  const myStats = React.useMemo(() => {
    if (!myResult?.answers) return { correct: 0, total: 0, pct: 0 };
    let correct = 0;
    myResult.answers.forEach((a) => {
      if (a.question?.options?.find((o) => o.id === a.option_id && o.is_answer)) correct++;
    });
    const total = myResult.answers.length;
    return { correct, total, pct: total ? Math.round((correct / total) * 100) : 0 };
  }, [myResult]);

  const bothFinished = myPoints !== null && myPoints !== undefined
    && oppPoints !== null && oppPoints !== undefined;
  let resultLabel = null;
  let iWon = false;
  if (bothFinished) {
    if (myPoints > oppPoints)      { resultLabel = "🏆 You Won!"; iWon = true; }
    else if (myPoints < oppPoints) { resultLabel = "😔 You Lost"; }
    else                           { resultLabel = "🤝 It's a Draw!"; }
  }

  const PlayerCard = ({ name, points, correct, total, pct, isMe, isWinner }) => {
    const borderColor = !bothFinished ? "#e0e0e0" : isWinner ? "#16a34a" : "#dc2626";
    const bgColor     = !bothFinished ? "transparent" : isWinner ? "#f0fdf4" : "#fff5f5";
    return (
      <Paper sx={{ p: 2.5, borderRadius: "16px", border: `2px solid ${borderColor}`, bgcolor: bgColor, position: "relative" }}>
        {bothFinished && (
          <Chip
            size="small"
            label={isWinner ? "WINNER" : myPoints === oppPoints ? "DRAW" : "LOSER"}
            sx={{
              position: "absolute", top: 8, right: 8,
              bgcolor: isWinner ? "#16a34a" : myPoints === oppPoints ? "#F59E0B" : "#dc2626",
              color: "#fff", fontWeight: 700, fontSize: "0.65rem",
            }}
          />
        )}
        <UserAvatar
          src={isMe ? mePlayer?.auth_profile?.photo : oppPlayer?.auth_profile?.photo}
          name={name}
          sx={{ width: 44, height: 44, mx: "auto", mb: 1 }}
        />
        <Typography variant="body2" sx={{ fontWeight: 700, mb: 1, textAlign: "center" }}>{name}</Typography>
        <Typography variant="h3" sx={{ fontWeight: 800, color: isMe ? "#E02424" : "#333", textAlign: "center" }}>
          {points ?? "—"} <Typography component="span" variant="caption" color="text.secondary">pts</Typography>
        </Typography>
        {pct !== undefined && (
          <Typography variant="body2" sx={{ textAlign: "center", mt: 0.5 }}>{pct}% · {correct}/{total} correct</Typography>
        )}
        {(points === null || points === undefined) && (
          <Box sx={{ textAlign: "center", mt: 1 }}>
            <CircularProgress size={20} sx={{ color: "#bbb" }} />
            <Typography variant="caption" display="block" color="text.secondary">Still playing…</Typography>
          </Box>
        )}
      </Paper>
    );
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth style={{ zIndex: 1500 }} PaperProps={{ sx: { borderRadius: "16px", p: 1 } }}>
      <DialogTitle sx={{ textAlign: "center", fontWeight: 800, fontSize: "1.3rem", pt: 3 }}>
        Duel Results
        {resultLabel && (
          <Typography variant="subtitle1" sx={{ fontWeight: 700, color: iWon ? "#16a34a" : myPoints === oppPoints ? "#F59E0B" : "#dc2626" }}>
            {resultLabel}
          </Typography>
        )}
      </DialogTitle>
      <DialogContent sx={{ pb: 3 }}>
        {(loadingDuel || loadingResult) ? (
          <Box sx={{ textAlign: "center", py: 4 }}><CircularProgress /></Box>
        ) : !duel ? (
          <Typography color="text.secondary" sx={{ textAlign: "center", py: 4 }}>Could not load duel data.</Typography>
        ) : (
          <>
            {!bothFinished && (
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: "center", mb: 2 }}>
                Waiting for opponent to finish…
              </Typography>
            )}
            <Grid container spacing={2} justifyContent="center" sx={{ mb: 2 }}>
              <Grid item xs={5}>
                <PlayerCard
                  name={myName}
                  points={myPoints}
                  correct={myStats.correct}
                  total={myStats.total}
                  pct={myStats.pct}
                  isMe
                  isWinner={bothFinished && myPoints > oppPoints}
                />
              </Grid>
              <Grid item xs={2} sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                <Typography variant="h5" sx={{ fontWeight: 800, color: "#666" }}>VS</Typography>
              </Grid>
              <Grid item xs={5}>
                <PlayerCard
                  name={oppName}
                  points={oppPoints}
                  isWinner={bothFinished && oppPoints > myPoints}
                />
              </Grid>
            </Grid>
            <Box sx={{ textAlign: "center" }}>
              <Button variant="outlined" onClick={onClose}>Close</Button>
            </Box>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

const QUIZ_TYPE_CONFIG = {
  practice: {
    icon: <SchoolIcon sx={{ fontSize: "2rem", color: "primary.safeOnLight" }} />,
    bgcolor: "#E8F4FF",
    label: "Practice",
    chipColor: "primary.safeOnLight",
    chipBg: "#E8F4FF",
  },
  learn: {
    icon: <AutoStoriesIcon sx={{ fontSize: "2rem", color: "#7C3AED" }} />,
    bgcolor: "#F3E8FF",
    label: "Learn",
    chipColor: "#7C3AED",
    chipBg: "#F3E8FF",
  },
  duel: {
    icon: <PeopleAltIcon sx={{ fontSize: "2rem", color: "#E02424" }} />,
    bgcolor: "#FFF5F5",
    label: "Duel",
    chipColor: "#E02424",
    chipBg: "#FFF5F5",
  },
  tournament: {
    icon: <EmojiEventsIcon sx={{ fontSize: "2rem", color: "#F59E0B" }} />,
    bgcolor: "#FFF8E1",
    label: "Tournament",
    chipColor: "#B45309",
    chipBg: "#FFF8E1",
  },
};

const getTypeConfig = (type) =>
  QUIZ_TYPE_CONFIG[type] || {
    icon: <SchoolIcon sx={{ fontSize: "2rem", color: "#666" }} />,
    bgcolor: "#f5f5f5",
    label: type || "Quiz",
    chipColor: "#666",
    chipBg: "#f5f5f5",
  };

const RecentQuiz = () => {
  const [sortBy, setSortBy] = React.useState("date");
  const [filterType, setFilterType] = React.useState("");
  const [openModals, setOpenModals] = React.useState({});
  const [selectedQuizId, setSelectedQuizId] = React.useState(null);
  const [correctQuiz, setCorrectQuiz] = React.useState(false);
  const [playTransitioning, setPlayTransitioning] = React.useState(false);
  const [duelResultQuizId, setDuelResultQuizId] = React.useState(null);
  const [tournamentLookupQuizId, setTournamentLookupQuizId] = React.useState(null);
  const [learnLessonQuizId, setLearnLessonQuizId] = React.useState(null);
  const [activeSession, setActiveSession] = React.useState(null);
  const [page, setPage] = React.useState(1);
  const itemsPerPage = 10;
  const { data, isLoading, isFetching } = useGetQuizzesPaginatedQuery({
    page,
    per_page: itemsPerPage,
    sort_by: sortBy,
    type: filterType || null,
    is_assigned: false,
  });
  const authData = useSelector((state) => state.auth);
  const userId = authData?.user?.id;
  const navigate = useNavigate();

  // Check for an interrupted practice quiz session on mount
  React.useEffect(() => {
    if (!userId) return;
    const session = getActiveQuizSession(userId);
    if (session?.quizId) setActiveSession(session);
  }, [userId]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);

    if (isNaN(date.getTime())) {
      throw new Error("Invalid date string");
    }

    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    });
  };

  const quizzes = data?.quizzes || [];

  const handleChangePage = (_event, newPage) => {
    setPage(newPage);
  };

  const handleChangeSort = (event) => {
    setSortBy(event.target.value);
    setPage(1);
  };

  const handleChangeFilter = (event) => {
    setFilterType(event.target.value);
    setPage(1);
  };

  const { data: quizResult, isError } = useGetQuizResultQuery(
    { quiz_id: selectedQuizId || 0 },
    { skip: !selectedQuizId }
  );

  const { data: tournamentByQuizData } = useGetTournamentByQuizIdQuery(
    tournamentLookupQuizId,
    { skip: !tournamentLookupQuizId }
  );

  React.useEffect(() => {
    if (tournamentByQuizData?.tournament_id) {
      navigate(`tournament/${tournamentByQuizData.tournament_id}`, { state: { defaultTab: 3 } });
      setTournamentLookupQuizId(null);
    }
  }, [tournamentByQuizData, navigate]);

  const handleOpenModal = (quizId) => {
    setSelectedQuizId(quizId);
    setOpenModals((prev) => ({ ...prev, [quizId]: true }));
    if (quizResult) {
      const quizIds = quizResult.answers?.map((answer) => answer.quiz_id) || [];
      setCorrectQuiz(quizIds.includes(quizId));
    }
  };

  const handleCloseModal = (quizId) => {
    setOpenModals((prev) => ({ ...prev, [quizId]: false }));
    setSelectedQuizId(null);
  };

  React.useEffect(() => {
    if (quizResult && selectedQuizId !== null) {
      const quizIds = quizResult.answers?.map((answer) => answer.quiz_id) || [];
      setCorrectQuiz(quizIds.includes(selectedQuizId));
      console.log(quizResult);
    }
  }, [quizResult, selectedQuizId]);

  const result = {
    correct: 0,
    wrong: 0,
  };

  quizResult?.answers?.forEach((answer) => {
    const correctOption = answer.question.options.find(
      (option) => option.id === answer.option_id && option.is_answer === true
    );

    if (correctOption) {
      result.correct += 1;
    } else {
      result.wrong += 1;
    }
  });

  const totalQuestions = quizResult?.answers?.length || 0;
  const percentage = (result.correct / totalQuestions) * 100;

  const totalQuizzes = data?.pagination?.total || 0;
  const totalPages = data?.pagination?.pages || 0;
  const startIndex = (page - 1) * itemsPerPage + 1;
  const endIndex = Math.min(page * itemsPerPage, totalQuizzes);

  return (
    <>
      {/* ── Resume banner: shown when a practice quiz was interrupted ── */}
      {activeSession && (
        <Box
          sx={{
            display: "flex", alignItems: "center", justifyContent: "space-between",
            gap: 1.5, flexWrap: "wrap",
            mb: 2.5, px: 2.5, py: 1.5,
            borderRadius: "14px",
            background: "linear-gradient(90deg, #FF6B00 0%, #FF9A00 100%)",
            boxShadow: "0 4px 16px rgba(255,107,0,0.3)",
          }}
        >
          <Box>
            <Typography sx={{ fontWeight: 800, color: "#fff", fontSize: "0.95rem" }}>
              You have an in-progress quiz{activeSession.subject ? ` — ${activeSession.subject}` : ""}
            </Typography>
            <Typography sx={{ color: "rgba(255,255,255,0.8)", fontSize: "0.78rem" }}>
              Continue from where you left off
            </Typography>
          </Box>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button
              size="small"
              variant="contained"
              onClick={() => navigate("play/practice/quiz")}
              sx={{ bgcolor: "#fff", color: "#FF6B00", fontWeight: 800, borderRadius: "10px", "&:hover": { bgcolor: "rgba(255,255,255,0.85)" } }}
            >
              Resume
            </Button>
            <Button
              size="small"
              onClick={() => { clearActiveQuizSession(userId); setActiveSession(null); }}
              sx={{ color: "rgba(255,255,255,0.8)", fontWeight: 700, borderRadius: "10px", "&:hover": { bgcolor: "rgba(255,255,255,0.12)" } }}
            >
              Dismiss
            </Button>
          </Box>
        </Box>
      )}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "auto",
            sm: "auto",
            md: "auto auto ",
            lg: "auto auto ",
          },
          gap: { xs: 2.5, sm: 2, md: 2, lg: 2.5 },
        }}
      >
        <CustomInstructionBox
          heading={"Game Rules"}
          textOne={"There are no rules — it's YOUR rules! 😎"}
          textTwo={"Play as much as you want, whenever you want"}
          textThree={"Your game, your way. Have fun!"}
        />
        <Box
          onClick={() => setPlayTransitioning(true)}
          sx={{
            background: "linear-gradient(145deg, #FF6B00 0%, #FF9A00 60%, #FFD000 100%)",
            borderRadius: "16px",
            padding: "2em",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            cursor: "pointer",
            gap: "0.75em",
            position: "relative",
            overflow: "hidden",
            transition: "all 0.25s ease",
            boxShadow: "0 6px 24px rgba(255,107,0,0.4)",
            "&::before": {
              content: '""',
              position: "absolute",
              top: "-40%",
              left: "-40%",
              width: "80%",
              height: "80%",
              background: "rgba(255,255,255,0.15)",
              borderRadius: "50%",
              pointerEvents: "none",
            },
            "&:hover": {
              transform: "translateY(-4px) scale(1.02)",
              boxShadow: "0 12px 32px rgba(255,107,0,0.55)",
            },
            "&:active": {
              transform: "scale(0.97)",
            },
          }}
        >
          <Box
            sx={{
              bgcolor: "rgba(255,255,255,0.18)",
              borderRadius: "50%",
              p: 1.5,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <SportsEsportsIcon
              sx={{
                fontSize: "3rem",
                color: "#fff",
                animation: `${joystickWiggle} 1.8s ease-in-out infinite`,
                animationDelay: "1s",
              }}
            />
          </Box>
          <Typography
            variant="body1"
            sx={{
              fontWeight: 800,
              fontSize: "1.25rem",
              color: "#fff",
              letterSpacing: "0.5px",
              textTransform: "uppercase",
            }}
          >
            Play
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: "rgba(255,255,255,0.8)", fontSize: "0.75rem", textAlign: "center" }}
          >
            Practice · Duel · Tournament
          </Typography>
        </Box>
      </Box>
      <Box sx={{ marginTop: "2em" }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "1em",
          }}
        >
          <Typography
            variant="h3"
            sx={{
              fontSize: { xs: "1rem", md: "1.25rem" },
              fontWeight: 700,
              letterSpacing: "-1px",
            }}
          >
            Recent Games
          </Typography>
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            <TextField
              select
              size="small"
              value={filterType}
              onChange={handleChangeFilter}
              sx={{ minWidth: 140 }}
              SelectProps={{ displayEmpty: true }}
            >
              <MenuItem value="">All Types</MenuItem>
              <MenuItem value="practice">Practice</MenuItem>
              <MenuItem value="learn">Learn Mode</MenuItem>
              <MenuItem value="duel">Duel</MenuItem>
              <MenuItem value="tournament">Tournament</MenuItem>
            </TextField>
          </Box>
        </Box>

        {(isLoading || isFetching) && <LinearProgress />}

        {quizzes.map((quiz) => {
          const typeConfig = getTypeConfig(quiz.type);
          const isLearn = quiz.type === "learn";
          return (
          <Box key={quiz.id}>
            <CustomInfoCard
              title={quiz.subject}
              date={formatDate(quiz.updated_at)}
              details={
                isLearn
                  ? `📖 Lesson: ${quiz.questions[0]?.topic || quiz.subject}`
                  : quiz.questions[0]?.topic
              }
              handleClick={() => {
                if (isLearn) {
                  setLearnLessonQuizId(quiz.id);
                  return;
                }
                if (quiz.answers) {
                  if (quiz.type === "duel") {
                    setDuelResultQuizId(quiz.id);
                  } else if (quiz.type === "tournament") {
                    setTournamentLookupQuizId(quiz.id);
                  } else {
                    handleOpenModal(quiz.id);
                  }
                } else {
                  // Untaken quiz — route to the appropriate player
                  if (quiz.type === "duel") {
                    navigate("play/duel");
                  } else if (quiz.type === "tournament") {
                    navigate("play/tournament");
                  } else {
                    // Practice / AI quiz: seed the restore session and open the player
                    saveActiveQuizSession(userId, { quizId: quiz.id, subject: quiz.subject });
                    setActiveSession({ quizId: quiz.id, subject: quiz.subject });
                    navigate("play/practice/quiz");
                  }
                }
              }}
              iconElement={
                <Box sx={{
                  width: 48, height: 48, borderRadius: "12px",
                  bgcolor: typeConfig.bgcolor,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  flexShrink: 0,
                }}>
                  {typeConfig.icon}
                </Box>
              }
              logo={
                <Chip
                  size="small"
                  label={typeConfig.label}
                  sx={{
                    bgcolor: typeConfig.chipBg,
                    color: typeConfig.chipColor,
                    fontWeight: 700,
                    fontSize: "0.7rem",
                    border: `1px solid ${typeConfig.chipColor}`,
                    height: 22,
                  }}
                />
              }
              action={
                isLearn ? (
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 0.6, alignItems: "flex-end" }}>
                    <Button
                      size="small"
                      variant="contained"
                      onClick={(e) => { e.stopPropagation(); setLearnLessonQuizId(quiz.id); }}
                      sx={{ bgcolor: "#7C3AED", color: "#fff", fontWeight: 700, fontSize: "0.72rem", borderRadius: "8px", px: 1.5, py: 0.4, minWidth: 0, "&:hover": { bgcolor: "#6d28d9" } }}
                    >
                      View Lesson
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (quiz.answers) {
                          handleOpenModal(quiz.id);
                        } else {
                          saveActiveQuizSession(userId, { quizId: quiz.id, subject: quiz.subject });
                          setActiveSession({ quizId: quiz.id, subject: quiz.subject });
                          navigate("play/practice/quiz");
                        }
                      }}
                      sx={{ borderColor: "#7C3AED", color: "#7C3AED", fontWeight: 700, fontSize: "0.72rem", borderRadius: "8px", px: 1.5, py: 0.4, minWidth: 0 }}
                    >
                      {quiz.answers ? "Quiz Result" : "Take Quiz"}
                    </Button>
                  </Box>
                ) : quiz.answers ? "View Result" : (quiz.type === "duel" ? "Join Duel" : quiz.type === "tournament" ? "Join Tournament" : "Take Quiz")
              }
            />

            <CustomResultModal
              open={openModals[quiz.id] || false}
              handleClose={() => handleCloseModal(quiz.id)}
              score={percentage.toFixed(1)}
              totalQuestions={totalQuestions}
              correctAnswers={result.correct}
              wrongAnswers={result.wrong}
              handleViewSolutions={() => {
                navigate(`view/${quiz.id}`, {
                  state: { quiz },
                });
              }}
              isLoading={!correctQuiz && !isError}
              isError={isError}
            />
          </Box>
          );
        })}

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            marginTop: "2em",
            alignItems: "center",
          }}
        >
          <Typography variant="body2" color="text.secondary">
            Showing {startIndex} to {endIndex} of {totalQuizzes} entries
          </Typography>

          <Pagination
            count={totalPages}
            page={page}
            onChange={handleChangePage}
            color="primary"
            shape="rounded"
            sx={{ color: "#CCCCCC" }}
          />
        </Box>

      </Box>

    {playTransitioning && (
      <PlayTransition
        onDone={() => {
          setPlayTransitioning(false);
          navigate("play");
        }}
      />
    )}

    <DuelResultModal
      open={!!duelResultQuizId}
      quizId={duelResultQuizId}
      onClose={() => setDuelResultQuizId(null)}
    />

    <LearnLessonModal
      open={!!learnLessonQuizId}
      quizId={learnLessonQuizId}
      onClose={() => setLearnLessonQuizId(null)}
    />
  </>
  );
};

export default RecentQuiz;
